// @flow
import React from 'react';
import {connect} from 'react-redux';

import {
  withTypedRouter,
  type WithRouterProps,
} from 'app/general/withTypedRouter';
import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {whenVisible} from 'app/utils/page-visibility';
import {
  runWhenAnyModelMatch,
  type ModelSubscribedComponentPropsType,
  type ModelChangeMessageType,
} from 'app/subscriptions';
import {withLoadingIndicator} from 'app/general/sweet-loading-bar';
import {ProcedureList} from 'app/procedures/components/ProcedureList';
import {LinkPagination} from 'app/components/general/LinkPagination';
import {fetchProceduresForDashboard} from 'app/procedures/actions';
import {StyledSidebarDIV} from 'app/components/general/Sidebar';
import MainCol from 'app/components/general/MainCol';
import {DashboardSidebarProcessesList} from 'app/components/general/DashboardPage/DashboardSidebarProcessesList';
import {DashboardSidebarPoliciesList} from 'app/components/general/DashboardPage/DashboardSidebarPoliciesList';
import {DashboardSidebarTagsList} from 'app/components/general/DashboardPage/DashboardSidebarTagsList';
import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import {gt, ngt} from 'app/utils/i18n';

import {getContentType} from 'app/utils/content-type';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {PROCEDURES_HOME} from 'app/urls';
import {SPPage} from 'app/general/structures/SPPage';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {CreateDocumentButton} from 'app/components/general/CreateDocumentButton';

import type {
  ProceduresDashboardLocationParamsType,
  ProcedureType,
} from 'app/procedures/types';
import type {StoredResponseType} from 'app/utils/redux_v2';
import {denormalizeProceduresList} from 'app/procedures/datafetchers';
import {convertLocationParamsToFetchParams} from 'app/procedures/actions';
import {shallowEqual} from 'app/utils/react-utils';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import type {StoreShapeType} from 'app/reducers/types';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';

const procedureCt = getContentType('procedure');

export const fetchParamsAreDifferent = (
  params: ProceduresDashboardLocationParamsType,
  nextParams: ProceduresDashboardLocationParamsType
) => {
  const fetchParams = convertLocationParamsToFetchParams(params);
  const nextFetchParams = convertLocationParamsToFetchParams(nextParams);
  return !shallowEqual(fetchParams, nextFetchParams);
};

type OwnProps = {||};

type ModelSubscribedOwnProps = {|
  ...OwnProps,
  ...ModelSubscribedComponentPropsType,
|};

type RouterProps = WithRouterProps<ProceduresDashboardLocationParamsType, {}>;

type RoutedModelSubscribedProps = {|
  ...ModelSubscribedOwnProps,
  ...RouterProps,
|};

type StateProps = {|
  procedures: StoredResponseType<ProcedureType>,
  storedFilters: ProceduresDashboardLocationParamsType,
|};

type DispatchProps = {|
  withLoadingIndicator: AsyncAction<typeof withLoadingIndicator, Promise<any>>,
  fetchProceduresForDashboard: AsyncAction<
    typeof fetchProceduresForDashboard,
    Promise<any>
  >,
|};

type Props = {|
  ...RoutedModelSubscribedProps,
  ...StateProps,
  ...DispatchProps,
|};

class BaseProceduresDashboard extends React.PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.props.onModelChange(this.handleModelChange);
  }

  UNSAFE_componentWillMount() {
    const {location, storedFilters, router} = this.props;

    if (
      !fetchParamsAreDifferent(location.query, {}) &&
      fetchParamsAreDifferent(location.query, storedFilters)
    ) {
      router.replace({
        pathname: location.pathname,
        query: storedFilters,
      });
    } else {
      this.fetchProcedures(this.props.location.query);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {query} = this.props.location;

    if (fetchParamsAreDifferent(query, nextProps.location.query)) {
      this.fetchProcedures(nextProps.location.query);
    }
  }

  handleModelChange = (message: ModelChangeMessageType) => {
    runWhenAnyModelMatch(message, ['procedure', 'procedureversion'], () =>
      this.fetchProcedures(this.props.location.query)
    );
  };

  fetchProcedures = whenVisible(
    (query: ProceduresDashboardLocationParamsType) => {
      const {withLoadingIndicator, fetchProceduresForDashboard} = this.props;
      return withLoadingIndicator(() => fetchProceduresForDashboard(query));
    }
  );

  resetFilters = () => {
    const {router, location} = this.props;
    router.replace({pathname: location.pathname});
  };

  render() {
    const {
      procedures,
      location: {query},
      router,
    } = this.props;
    const viewingAll = !fetchParamsAreDifferent({}, query);

    return (
      <SPPage
        pageTitle={gt('Procedures')}
        actionArea={({isTeamMember}) => (
          <CreateDocumentButton
            contentType="procedure"
            disabled={!isTeamMember}
          />
        )}
      >
        {({isTeamMember, isSuper}) => (
          <div className="row">
            <BootstrapMinMediumMediaQuery>
              <StyledSidebarDIV>
                <DashboardSidebarFoldersList />
                <DashboardSidebarProcessesList
                  canCreate={isTeamMember || isSuper}
                />
                <DashboardSidebarPoliciesList
                  canCreate={isTeamMember || isSuper}
                />
                <DashboardSidebarTagsList />
              </StyledSidebarDIV>
            </BootstrapMinMediumMediaQuery>
            <MainCol>
              <Filterbar
                ct="procedure"
                resetFilters={this.resetFilters}
                viewingAll={viewingAll}
                show={{
                  team: true,
                  tags: true,
                  sort: true,
                  filters: true,
                  search: true,
                }}
              />

              {!procedures.loading ? (
                <DashboardItemsCounter
                  text={ngt('%s procedure', '%s procedures', procedures.count)}
                  showSearchHelp={query.search != null}
                />
              ) : null}

              <ProcedureList
                procedures={procedures}
                resetFilters={this.resetFilters}
                refreshData={() => this.fetchProcedures(query)}
                canCreate={isTeamMember}
                ordering={query.ordering}
                router={router}
                query={{
                  team_id: query.team_id,
                  search: query.search,
                  tag: query.tag,
                  filter: query.filter,
                }}
                folder_hashid={query.folder}
                viewingAll={viewingAll}
              />
              {!procedures.loading ? (
                <LinkPagination page={query.page} response={procedures} />
              ) : null}
            </MainCol>
          </div>
        )}
      </SPPage>
    );
  }
}

const mapStateToProps = (state, {location}): StateProps => ({
  procedures: denormalizeProceduresList(
    state,
    convertLocationParamsToFetchParams(location.query)
  ),
  storedFilters: state.proceduresPage
    .getIn(
      ['dashboard', 'currentFilters']
      // $FlowFixMe
    )
    .toJS(),
});

const mapDispatchToProps: DispatchProps = {
  // $FlowFixMe
  fetchProceduresForDashboard,
  // $FlowFixMe
  withLoadingIndicator,
};

export const ProceduresDashboard = keepUpdatedEnhancer<OwnProps>(
  withTypedRouter<ModelSubscribedOwnProps, RouterProps>(
    connect<
      Props,
      RoutedModelSubscribedProps,
      StateProps,
      DispatchProps,
      StoreShapeType,
      _
    >(
      mapStateToProps,
      mapDispatchToProps
    )(BaseProceduresDashboard)
  )
);

export const ProceduresDashboardBreadcrumb = (props: any) => (
  <BreadcrumbItemStructure
    {...props}
    icon={procedureCt.icon}
    text={gt('Procedures')}
    link={PROCEDURES_HOME}
  />
);
